import { Icon } from '@chakra-ui/react';
import { Box } from '@components/ui/atoms/Box';
import { Flex } from '@components/ui/atoms/Flex';
import { Group } from '@components/ui/atoms/Group';
import { ChevronDownIcon } from '@components/ui/atoms/Icons';
import { List } from '@components/ui/atoms/List';
import { Stack } from '@components/ui/atoms/Stack';
import { Text } from '@components/ui/atoms/Text';
import { Tooltip } from '@components/ui/molecules/Tooltip';
import { memo, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router';

import { MenuItem } from './MenuItem';
import { MenuGroupProvider, useMenuGroupContext } from './Provider';
import type { SidebarMenuItem } from './types';

interface Props {
  menuItem: SidebarMenuItem;
  isExpanded: boolean;
}

export const MenuGroup: React.FC<Props> = memo(({ menuItem, isExpanded }) => {
  return (
    <MenuGroupProvider menuItem={menuItem}>
      <Component menuItem={menuItem} isExpanded={isExpanded} />
    </MenuGroupProvider>
  );
});

const Component: React.FC<Props> = memo(({ isExpanded }) => {
  const {
    ref,
    isMenuExpanded,
    isHovering,
    toggleMenu,
    isActive,
    menuItem: { icon, label, children, url },
  } = useMenuGroupContext();
  const navigate = useNavigate();

  const handleMenuClick = useCallback(() => {
    if (url) void navigate(url);

    if (children?.length) toggleMenu();
  }, [url, navigate, children?.length, toggleMenu]);

  const shouldHighlight = useMemo(() => isActive || isHovering, [isActive, isHovering]);
  const expandMenu = useMemo(() => isMenuExpanded, [isMenuExpanded]);

  const shouldShowChildren = useMemo(() => expandMenu, [expandMenu]);

  const renderMenuItems = useCallback(() => {
    if (!children || children.length === 0) return null;

    return children.map((child) => (
      <MenuItem key={child.id} menuItem={child} isExpanded={isExpanded} />
    ));
  }, [children, isExpanded]);

  const iconSize = '16px';
  const renderedIcon = useMemo(() => {
    if (!icon) return null;

    return (
      <Icon
        display="flex"
        justifyContent="center"
        alignItems="center"
        css={{ width: iconSize, height: iconSize }}
        color={shouldHighlight ? 'brand' : 'body.dark'}
      >
        {icon(shouldHighlight ? 'active' : 'inactive')}
      </Icon>
    );
  }, [icon, shouldHighlight, iconSize]);

  // Memoize label render.
  const renderedLabel = useMemo(() => {
    if (!isExpanded) return null;

    return (
      <Text
        fontSize="14px"
        lineHeight="normal"
        color={shouldHighlight ? 'brand' : 'body.dark'}
        textStyle="bodyThree"
        fontWeight={shouldHighlight ? '700' : '500'}
      >
        {label}
      </Text>
    );
  }, [isExpanded, label, shouldHighlight]);

  const renderedToggleIcon = useMemo(() => {
    if (!children?.length || !isExpanded) return null;

    return (
      <Icon
        as={ChevronDownIcon}
        w="12px"
        h="12px"
        color={shouldHighlight ? 'title.dark' : 'body.dark'}
        transform={isMenuExpanded ? 'rotate(0deg)' : 'rotate(-90deg)'}
        transition="transform 0.2s"
      />
    );
  }, [children?.length, isExpanded, isMenuExpanded, shouldHighlight]);

  const groupHeader = (
    <Flex
      justifyContent={isExpanded ? 'space-between' : 'center'}
      alignItems="center"
      onClick={handleMenuClick}
      aria-label={!isExpanded ? label : undefined}
      position="relative"
      px={isExpanded ? '20px' : '0'}
      py="12px"
      gap="8px"
      _before={
        shouldHighlight
          ? {
              content: '""',
              position: 'absolute',
              left: 0,
              top: '50%',
              transform: 'translateY(-50%)',
              width: '3px',
              height: '16px',
              backgroundColor: 'brand',
              borderRadius: '1px',
            }
          : {}
      }
    >
      <Group display="flex" justifyContent="center" alignItems="center" gap="8px">
        {renderedIcon}
        {renderedLabel}
      </Group>
      {renderedToggleIcon}
    </Flex>
  );

  return (
    <Box ref={ref} w="full" height="fit" css={{ minHeight: '44px' }} cursor="button">
      <List as="ul" listStyleType="none">
        {isExpanded ? (
          groupHeader
        ) : (
          <Tooltip content={label} positioning={{ placement: 'right' }} showArrow>
            <Box display="block" w="full">
              {groupHeader}
            </Box>
          </Tooltip>
        )}
        {shouldShowChildren && children && children.length > 0 && (
          <Stack
            pl={isExpanded ? '56px' : '0'}
            gapY="24px"
            py={isExpanded ? '4px' : '8px'}
            alignItems={isExpanded ? 'flex-start' : 'center'}
          >
            {renderMenuItems()}
          </Stack>
        )}
      </List>
    </Box>
  );
});

Component.displayName = 'MenuGroupComponent';
MenuGroup.displayName = 'MenuGroup';
